/**
 * Turning a server's field errors into something a person can act on.
 *
 * The forms used to answer "One field needs attention." and leave you to find
 * it. On a form with ten inputs — and worse, on one with a repeater holding a
 * programme day's worth of them — that is a hunt, and the message that starts
 * it knows the answer already.
 *
 * So each problem is paired with the human name of the field it belongs to. A
 * problem with no path belongs to the record rather than to any input (the
 * dates being out of order is about the pair, not about either box) and is
 * deliberately left unattached: naming a field there would point at the wrong
 * one.
 */

import type { FieldProblem } from "@/lib/types";

export interface NamedProblem {
  /** The field's label, or null when the problem belongs to the whole record. */
  field: string | null;
  message: string;
}

/**
 * Each problem with its field named.
 *
 * `labelFor` resolves a field's own name to what the form calls it on screen.
 * When it has no answer the raw name is used rather than nothing: the server
 * can report a field this form does not render, and dropping it would leave a
 * count nobody can act on — which is the failure being fixed.
 */
export function namedProblems(
  problems: readonly FieldProblem[],
  labelFor: (name: string) => string | undefined,
): NamedProblem[] {
  return problems.map((problem) => {
    const [first, ...rest] = problem.path;
    if (first === undefined) return { field: null, message: problem.message };

    const crumbs = [typeof first === "string" ? (labelFor(first) ?? first) : String(first)];
    for (const step of rest) {
      if (typeof step === "number") {
        // A row number belongs to the repeater it indexes — "Programme days 2"
        // rather than a crumb of its own. And it counts from one, because row
        // 0 in the path is the first row on screen and "days 0" sends somebody
        // to the second one, or to none.
        crumbs[crumbs.length - 1] = `${crumbs.at(-1)} ${step + 1}`;
      } else {
        crumbs.push(step);
      }
    }
    return { field: crumbs.join(" › "), message: problem.message };
  });
}
